import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileGuidesOpen, setMobileGuidesOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    function handleScroll() {
      if (navRef.current) {
        if (window.scrollY > 50) {
          navRef.current.classList.add('bg-background-50/95', 'backdrop-blur-md', 'border-b', 'border-background-200/60');
          navRef.current.classList.remove('bg-transparent');
        } else {
          navRef.current.classList.remove('bg-background-50/95', 'backdrop-blur-md', 'border-b', 'border-background-200/60');
          navRef.current.classList.add('bg-transparent');
        }
      }
    }
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeMobile = () => {
    setMobileOpen(false);
    setMobileGuidesOpen(false);
  };

  const guideArticles = articles.filter((a) =>
    ['sugargoo-review-2026', 'sugargoo-shipping-cost-guide', 'sugargoo-coupon-discount-guide', 'sugargoo-spreadsheet-ultimate-guide'].includes(a.slug)
  );

  return (
    <nav ref={navRef} className="fixed top-0 left-0 right-0 z-50 transition-all duration-300">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 group">
            <span className="w-8 h-8 flex items-center justify-center bg-primary-500 text-background-50 rounded-lg">
              <i className="ri-file-list-3-line text-lg"></i>
            </span>
            <span className="font-heading text-lg text-foreground-900 whitespace-nowrap">Sugargoo Sheet</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap font-medium">Home</Link>
            <Link to="/article/sugargoo-spreadsheet-ultimate-guide" className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap">Shopping Guide</Link>
            <a href="#categories" className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap">Categories</a>
            <Link to="/article/sugargoo-review-2026" className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap">Review</Link>
            <Link to="/article/sugargoo-shipping-cost-guide" className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap">Shipping</Link>
            <Link to="/article/sugargoo-coupon-discount-guide" className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap">Coupons</Link>
            <a href="#faq" className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap">FAQ</a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Visit Store
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`text-xl text-foreground-700 ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden pb-6 border-t border-background-200/60 pt-4 max-h-[70vh] overflow-y-auto">
            <div className="flex flex-col gap-1">
              <Link to="/" onClick={closeMobile} className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm text-foreground-700 hover:bg-background-100 transition-colors font-medium">
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className="ri-home-4-line"></i>
                </span>
                Home
              </Link>

              <Link to="/article/sugargoo-spreadsheet-ultimate-guide" onClick={closeMobile} className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm text-foreground-700 hover:bg-background-100 transition-colors">
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                  <i className="ri-book-open-line"></i>
                </span>
                Shopping Guide
              </Link>

              <a href="#categories" onClick={closeMobile} className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm text-foreground-700 hover:bg-background-100 transition-colors">
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
                  <i className="ri-grid-line"></i>
                </span>
                Categories
              </a>

              {/* Mobile Category List */}
              <div className="ml-3 pl-3 border-l-2 border-background-200/60 space-y-0.5 mt-1 mb-2">
                {categories.map((cat) => (
                  <a
                    key={cat.slug}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={closeMobile}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-foreground-500 hover:bg-background-100 hover:text-foreground-800 transition-colors cursor-pointer"
                  >
                    <i className={`${cat.icon} text-foreground-400`}></i>
                    {cat.name}
                  </a>
                ))}
              </div>

              {/* Mobile Guides */}
              <button
                onClick={() => setMobileGuidesOpen(!mobileGuidesOpen)}
                className="flex items-center justify-between gap-3 px-3 py-3 rounded-lg text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer w-full"
              >
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className="ri-guide-line"></i>
                  </span>
                  SEO Guides
                </div>
                <i className={`ri-arrow-right-s-line text-xs text-foreground-400 transition-transform ${mobileGuidesOpen ? 'rotate-90' : ''}`}></i>
              </button>

              {mobileGuidesOpen && (
                <div className="ml-3 pl-3 border-l-2 border-background-200/60 space-y-0.5 mb-2">
                  {guideArticles.map((g) => (
                    <Link
                      key={g.slug}
                      to={`/article/${g.slug}`}
                      onClick={closeMobile}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-foreground-500 hover:bg-background-100 hover:text-foreground-800 transition-colors"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-accent-400 flex-shrink-0"></span>
                      {g.title.length > 50 ? g.title.substring(0, 50) + '...' : g.title}
                    </Link>
                  ))}
                </div>
              )}

              <a href="#faq" onClick={closeMobile} className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm text-foreground-700 hover:bg-background-100 transition-colors">
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
                  <i className="ri-question-line"></i>
                </span>
                FAQ
              </a>

              <div className="pt-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeMobile}
                  className="flex items-center justify-center gap-2 w-full px-5 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Visit Store
                  <i className="ri-arrow-right-up-line"></i>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}